import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchPsap,
  PSAPInfo,
} from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import { fetchUser, fetchAllPermissions, fetchAppRoles } from '@rsos/sinatra';
import { DatasourceManagementState } from '.';
import { fetchSpatialLayers } from './spatialLayersSlice';

const CAPSTONE = 'capstone';

export type InitializationState = {
  initializing: boolean;
  initialized: boolean;
  initError?: string | null;
};

const initialState: InitializationState = {
  initializing: true,
  initialized: false,
  initError: null,
};

export const initialize = createAsyncThunk<
  PSAPInfo,
  void,
  { state: DatasourceManagementState }
>(
  'appDataSource/initialize',

  async (_, { dispatch, getState }) => {
    const isLoggedIn = getState().sinatra.user.isLoggedIn;
    return await dispatch(fetchUser())
      .then(() => {
        const foundOrg = organizationSelector(getState());
        const foundRole = foundOrg?.roles.find(
          role => role.application === CAPSTONE,
        );

        if (
          foundOrg &&
          foundRole &&
          foundRole.application &&
          foundRole.permissions &&
          foundRole.permissions.includes('MANAGE_DATA_SOURCES')
        ) {
          dispatch(fetchAllPermissions(foundOrg.id, foundRole.application));
          dispatch(fetchAppRoles(foundOrg.id, foundRole.application));
          dispatch(fetchSpatialLayers());
          dispatch(fetchPsap(`/psaps/${foundOrg.name}`));
          return getState().psaps.currentPSAP;
        } else {
          throw new Error('User does not have permissions to access');
        }
      })
      .catch(err => {
        if (isLoggedIn) {
          if (err) {
            throw new Error(err);
          }
        }
        window.location.assign('/');
        throw new Error('Not logged in');
      });
  },
);

const appSlice = createSlice({
  name: 'appDataSource',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(initialize.pending, state => {
        state.initializing = true;
        state.initialized = false;
        state.initError = null;
      })
      .addCase(initialize.fulfilled, state => {
        state.initializing = false;
        state.initialized = true;
        state.initError = null;
      })
      .addCase(initialize.rejected, (state, { error = {} }) => {
        state.initializing = false;
        state.initialized = false;
        state.initError = error.message;
      });
  },
});

export const appReducer = appSlice.reducer;
export default appReducer;
